import {verifyOriginal} from './originalVerification.js'
import {getSessionGeneration} from '../../lib/sessionGeneration.js'

// Recover a successful upload before retrying its separate selection operation.
// No automatic re-upload follows an uncertain response. Current server version
// and exact revision/spec binding fence both steps, including competing tabs.
export async function importOriginal({api: client, jobId, engineRevision, specFingerprint, file, provenance, digest = bytes => crypto.subtle.digest('SHA-256', bytes), currentSession = getSessionGeneration, isCurrent = () => true}) {
  const session = currentSession()
  const assertCurrent = () => {if (currentSession() !== session || !isCurrent()) throw new Error('The session or artwork view changed. No further import step will run; refresh to inspect any saved original.')}
  const hash = [...new Uint8Array(await digest(await file.arrayBuffer()))].map(n => n.toString(16).padStart(2, '0')).join('')
  assertCurrent()
  const response = await client.job(jobId), job = response.job
  assertCurrent()
  if (!job || job.artworkEngine?.revision !== engineRevision || job.artworkEngine?.specFingerprint !== specFingerprint) throw new Error('The saved revision changed. Refresh before importing this original.')
  const matches = asset => asset.role === 'artwork_engine_output' && asset.engineRevision === engineRevision && asset.specFingerprint === specFingerprint && asset.sha256 === hash && asset.bytes === file.size
  let asset = job.assets?.find(matches), current = job
  if (asset && asset.id === job.artworkEngine.outputAssetId) {
    await verifyOriginal(await client.asset(jobId, asset.id), hash, digest)
    assertCurrent()
    return {job, recovered: true, selected: true}
  }
  if (asset && asset.source !== 'original_file_import') throw new Error('This original has another provenance. Review the preserved asset; do not relabel or upload it again.')
  const recovered = Boolean(asset)
  if (!asset) {
    let saved
    try {assertCurrent();saved = await client.upload(job, 'artwork_engine_output', file, {engineRevision});assertCurrent()}
    catch {throw new Error('Upload outcome is uncertain. Refresh and retry this same file to inspect saved assets before any new upload.')}
    current = saved.job
    asset = current?.assets?.find(matches)
    if (!asset) throw new Error('Upload receipt is missing its exact original binding. Refresh and inspect saved assets before retrying.')
  }
  await verifyOriginal(await client.asset(jobId, asset.id), hash, digest)
  assertCurrent()
  try {
    const linked = await client.event(current, {type: 'artwork_engine_output_linked', engineRevision, outputAssetId: asset.id, provenance})
    assertCurrent()
    if (!linked?.job) throw new Error('Missing selection receipt')
    return {...linked, recovered, selected: true}
  } catch {throw new Error('Original bytes are saved but selection is unconfirmed. Retry this same file to recover the existing asset without uploading it twice.')}
}
